import { z } from 'zod'
import type { NoContext } from '~/services/api/contracts/NoContext'
import { phone } from '~/services/api/contracts/phone'
import {
  DeliveryContactTypeSchema,
  OrderModificationRequestStoreRequestSchema
} from '~/services/api/generated'
import { strictify } from '~/utils/strictify'
import { fieldNormalizer } from '~/services/api/contracts/fieldNormalizer'

const NULLABLE_DETAIL_TEXT = [
  'delivery_business_name',
  'delivery_contact_person',
  'delivery_phone',
  'delivery_contact_phone',
  'delivery_suite',
  'special_instructions'
] as const

// StoreOrderModificationRequest::prepareForValidation and ConvertEmptyStringsToNull
const normalize = fieldNormalizer({
  records: [
    {
      key: 'details',
      spec: {
        blankToNull: NULLABLE_DETAIL_TEXT,
        trimmed: ['delivery_address'],
        enums: [
          {
            field: 'delivery_contact_type',
            options: DeliveryContactTypeSchema.options,
            skipNull: true
          }
        ]
      }
    }
  ]
})

const base = OrderModificationRequestStoreRequestSchema
const schema = strictify(
  base.extend({
    details: base.shape.details
      .unwrap()
      .extend({ delivery_contact_phone: phone.nullish(), delivery_phone: phone.nullish() })
      .optional()
  })
)

export const OrderModificationRequestStore = (
  _context: NoContext
): z.ZodType<z.output<typeof schema>> =>
  z.preprocess(normalize, schema).superRefine((value, issues) => {
    // StoreOrderModificationRequest rules: details and items are each required without the other
    const hasItems = Array.isArray(value.items) && value.items.length > 0
    const details = value.details
    const hasDetails = details !== undefined && details !== null
    if (hasDetails && Object.keys(details).length === 0) {
      issues.addIssue({
        code: 'custom',
        message: 'details-must-not-be-empty',
        path: ['details']
      })
    }
    if (!hasDetails && !hasItems) {
      issues.addIssue({
        code: 'custom',
        message: 'details-required-without-items',
        path: ['details']
      })
      issues.addIssue({
        code: 'custom',
        message: 'items-required-without-details',
        path: ['items']
      })
    }
  })
